import { getBookingEngineApiUrl } from '@config/index';
import OrganizationAvailability from '@data/models/OrganizationAvailability';
import OrganizationDetail from '@data/models/OrganizationDetail';
import queryClient from '../queryClient';
import { apiCall } from '../axios';
export class OrganizationService {
    /**
     * GET /organizations/:id/availability
     *
     * Lightweight pricing and vacancy data for a date range, scoped to every
     * property under the organization in the URL — `mfd#10544`. The endpoint
     * takes snake_case query params (`check_in`/`check_out`) and requires both
     * together — verified against the local backend; it 400s given only one or
     * neither. Routed through the shared query client so repeated calls with
     * the same stay/currency within staleTime reuse the cached response instead
     * of re-hitting the network (e.g. toggling the sticky bar back to a date
     * already searched).
     */
    static getAvailability(organizationId, params) {
        return queryClient.fetchQuery({
            queryFn: () => apiCall(OrganizationAvailability)('get', `/organizations/${organizationId}/availability`, {
                check_in: params.checkIn,
                check_out: params.checkOut,
                currency: params.currency,
            }, { baseURL: getBookingEngineApiUrl() }),
            queryKey: [
                'organization-availability',
                organizationId,
                params.checkIn,
                params.checkOut,
                params.currency,
            ],
            // Pricing/vacancy can shift; kept short so it still revalidates
            // reasonably often while collapsing rapid-fire toggles.
            staleTime: 30 * 1000,
        });
    }
    /**
     * GET /booking-engine/v1/organizations/:id
     *
     * Organization metadata and static property data. Lives outside the default
     * `/booking` base, so it passes its own `baseURL`. Cached the same way as
     * availability, with a longer staleTime since this data changes rarely.
     */
    static getDetail(organizationId, lang = 'en', currency) {
        return queryClient.fetchQuery({
            queryFn: () => apiCall(OrganizationDetail)('get', `/organizations/${organizationId}`, { currency, lang }, { baseURL: getBookingEngineApiUrl() }),
            queryKey: ['organization-detail', organizationId, lang, currency],
            staleTime: 5 * 60 * 1000,
        });
    }
}
